/**
 * Serviço de API para comunicação com o back-end
 * Todas as requisições passam por aqui para proteger credenciais sensíveis
 */

import { 
  ApiResponse as ApiResponseType, 
  LoginRequest, 
  LoginResponse, 
  ValidationResponse, 
  FileUploadResponse,
  WhatsAppMessageRequest,
  WhatsAppMediaRequest,
  InstanceStatusResponse,
  DashboardMetrics,
  CalendarSettings,
  ConversationMemoryParams,
  ScheduleBlockParams,
  AppointmentParams,
  LeadParams
} from '../types/api';
import { Doctor, Clinic, Lead, Appointment, Attendee, User } from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export interface ApiResponse<T = any> {
  sucesso: boolean;
  dados?: T;
  mensagem?: string;
  detalhes?: string;
  timestamp?: string;
}

class ApiService {
  private isRedirecting = false;

  // Namespaces organizados
  public auth = {
    login: (email: string, senha: string) => this.login(email, senha),
    logout: () => this.logout(),
    getSession: () => this.getSession(),
    validateInternalSession: (token: string) => this.validateInternalSession(token),
    invalidateInternalSession: (token: string) => this.invalidateInternalSession(token),
    register: (userData: Partial<User>) => this.register(userData)
  };

  public doctors = {
    get: (params?: any) => this.getDoctors(params),
    getAll: (params?: string) => this.getDoctors(params),
    getById: (id: number) => this.getDoctor(id),
    create: (doctorData: Partial<Doctor>) => this.createDoctor(doctorData),
    update: (id: number, doctorData: Partial<Doctor>) => this.updateDoctor(id, doctorData),
    delete: (id: number) => this.deleteDoctor(id)
  };

  public clinics = {
    get: () => this.getClinics(),
    getAll: () => this.getClinics(),
    getById: (id: string, params?: string) => this.getClinicById(id, params),
    create: (clinicData: Partial<Clinic>) => this.createClinic(clinicData),
    update: (id: string, clinicData: Partial<Clinic>) => this.updateClinic(parseInt(id), clinicData),
    delete: (id: string) => this.deleteClinic(parseInt(id))
  };

  public attendees = {
    getAll: (params?: string) => this.getAttendees(params),
    create: (attendeeData: Partial<Attendee>) => this.createAttendee(attendeeData),
    update: (id: number, attendeeData: Partial<Attendee>) => this.updateAttendee(id, attendeeData),
    delete: (id: number) => this.deleteAttendee(id)
  };

  public users = {
    getByEmail: (params: string) => this.getUserByEmail(params),
    create: (userData: Partial<User>) => this.createUser(userData)
  };

  public leads = {
    get: (params?: LeadParams | string) => this.getLeads(params),
    getById: (id: string) => this.getLeadById(parseInt(id)),
    create: (leadData: Partial<Lead>) => this.createLead(leadData),
    update: (id: string, leadData: Partial<Lead>) => this.updateLead(parseInt(id), leadData),
    delete: (id: string) => this.deleteLead(parseInt(id))
  };

  public appointments = {
    get: (params?: AppointmentParams) => this.getAppointments(params),
    getById: (id: number) => this.request(`/api/appointments/${id}`),
    create: (appointmentData: Partial<Appointment>) => this.createAppointment(appointmentData),
    update: (id: number, appointmentData: Partial<Appointment>) => this.updateAppointment(id, appointmentData),
    delete: (id: number) => this.deleteAppointment(id),
    markReminderSent: (id: number) => this.request(`/api/appointments/${id}/mark-reminder-sent`, { method: 'PUT' })
  };

  public files = {
    get: (clinicId?: number) => this.getFiles(clinicId),
    register: (fileData: Record<string, unknown>) => this.registerFile(fileData),
    delete: (id: number) => this.deleteFile(id),
    upload: (file: File, endpoint?: string) => this.uploadFile(file, endpoint)
  };

  public clinicSettings = {
    get: (clinicId: number) => this.getClinicSettings(clinicId),
    save: (settingsData: Record<string, unknown>) => this.saveClinicSettings(settingsData),
    create: (settingsData: Record<string, unknown>) => this.saveClinicSettings(settingsData),
    update: (clinicId: number, settingsData: Record<string, unknown>) => this.saveClinicSettings(settingsData)
  };

  public reminderLogs = {
    get: (appointmentId?: number) => this.getReminderLogs(appointmentId),
    create: (logData: Record<string, unknown>) => this.createReminderLog(logData)
  };

  public conversationMemory = {
    get: (params?: ConversationMemoryParams) => this.getConversationMemory(params),
    save: (messageData: Record<string, unknown>) => this.saveConversationMemory(messageData),
    clear: (leadId: number) => this.clearConversationMemory(leadId)
  };

  public scheduleBlocks = {
    get: (params?: ScheduleBlockParams) => this.getScheduleBlocks(params),
    create: (blockData: Record<string, unknown>) => this.createScheduleBlock(blockData),
    update: (id: number, blockData: Record<string, unknown>) => this.updateScheduleBlock(id, blockData),
    delete: (id: number) => this.deleteScheduleBlock(id)
  };

  public conversations = {
    getById: (id: string) => this.request<any>(`/conversations/${id}`),
    update: (id: string, data: any) => this.request<any>(`/conversations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  };

  public instances = {
    get: (params?: string) => {
      const queryString = params ? `?${params}` : '';
      return this.request<any>(`/instances${queryString}`);
    },
    getById: (id: string) => this.request<any>(`/instances/${id}`)
  };

  /**
   * Métodos genéricos HTTP
   */
  public get = <T = any>(endpoint: string): Promise<ApiResponse<T>> => {
    return this.request<T>(endpoint);
  };

  public post = <T = any>(endpoint: string, data: any): Promise<ApiResponse<T>> => {
    const isFormData = data instanceof FormData;
    return this.request<T>(endpoint, {
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
      headers: isFormData ? {} : { 'Content-Type': 'application/json' }
    });
  };

  public put = <T = any>(endpoint: string, data: any): Promise<ApiResponse<T>> => {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  };

  /**
   * Método genérico para fazer requisições
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${API_URL}${endpoint}`;

    const config: RequestInit = {
      ...options,
      credentials: 'include', // IMPORTANTE: incluir cookies httpOnly
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      // Verificar se a resposta é JSON válida
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error('Resposta inválida do servidor');
      }

      // Detectar sessão expirada e redirecionar para login
      if (response.status === 401 && !this.isRedirecting) {
        const isSessionError = data.mensagem?.includes('Sessão') || 
                              data.mensagem?.includes('sessão') ||
                              data.mensagem?.includes('expirada') ||
                              data.mensagem?.includes('inválida') ||
                              data.mensagem?.includes('Acesso negado');
        
        if (isSessionError) {
          this.isRedirecting = true;
          
          // Limpar qualquer estado local se necessário
          localStorage.removeItem('user');
          
          // Redirecionar para login
          if (window.location.pathname !== '/login') {
            console.log('Sessão expirada. Redirecionando para login...');
            window.location.href = '/login';
          }
          
          throw new Error('Sessão expirada. Faça login novamente.');
        }
      }

      if (!response.ok) {
        throw new Error(data.mensagem || `Erro HTTP ${response.status}`);
      }

      return data;
    } catch (error: any) {
      // Não logar erros de sessão inválida (comportamento esperado quando não logado)
      const isSessionError = error.message?.includes('Sessão') || 
                            error.message?.includes('sessão') ||
                            error.message?.includes('Acesso negado');
      
      if (!isSessionError) {
        console.error(`Erro na API [${endpoint}]:`, error);
      }
      throw error;
    }
  }

  // ==================== AUTENTICAÇÃO ====================

  async login(email: string, senha: string) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, senha }),
    });
  }

  async logout() {
    return this.request('/auth/logout', {
      method: 'POST',
    });
  }

  async getSession() {
    return this.request('/auth/session');
  }

  async validateInternalSession(token: string) {
    return this.request('/auth/validate-internal-session', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async invalidateInternalSession(token: string) {
    return this.request('/auth/invalidate-internal-session', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async register(userData: any) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async getUserByEmail(params: string) {
    return this.request(`/api/users/by-email?${params}`);
  }

  async createUser(userData: any) {
    return this.request('/api/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  // ==================== LEADS ====================

  async getLeads(params?: string | { clinic_id?: number; stage?: string }) {
    if (typeof params === 'string') {
      return this.request(`/api/leads?${params}`);
    }
    const query = new URLSearchParams(params as any).toString();
    return this.request(`/api/leads${query ? `?${query}` : ''}`);
  }

  async getLeadById(id: number) {
    return this.request(`/api/leads/${id}`);
  }

  async createLead(leadData: any) {
    return this.request('/api/leads', {
      method: 'POST',
      body: JSON.stringify(leadData),
    });
  }

  async updateLead(id: number, leadData: any) {
    return this.request(`/api/leads/${id}`, {
      method: 'PUT',
      body: JSON.stringify(leadData),
    });
  }

  async deleteLead(id: number) {
    return this.request(`/api/leads/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== AGENDAMENTOS ====================

  async getAppointments(params?: AppointmentParams | any) {
    const query = new URLSearchParams(params as any).toString();
    return this.request(`/api/appointments${query ? `?${query}` : ''}`);
  }

  async createAppointment(appointmentData: any) {
    return this.request('/api/appointments', {
      method: 'POST',
      body: JSON.stringify(appointmentData),
    });
  }

  async updateAppointment(id: number, appointmentData: any) {
    return this.request(`/api/appointments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(appointmentData),
    });
  }

  async deleteAppointment(id: number) {
    return this.request(`/api/appointments/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== CLÍNICAS ====================

  async getClinics() {
    return this.request('/api/clinics');
  }

  async getClinicById(id: string, params?: string) {
    return this.request(`/api/clinics/${id}${params ? `?${params}` : ''}`);
  }

  async createClinic(clinicData: any) {
    return this.request('/api/clinics', {
      method: 'POST',
      body: JSON.stringify(clinicData),
    });
  }

  async updateClinic(id: number, clinicData: any) {
    return this.request(`/api/clinics/${id}`, {
      method: 'PUT',
      body: JSON.stringify(clinicData),
    });
  }

  async deleteClinic(id: number) {
    return this.request(`/api/clinics/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== INSTÂNCIAS WHATSAPP ====================

  async getInstances() {
    return this.request('/api/instances');
  }

  async createInstance(instanceData: any) {
    return this.request('/api/instances', {
      method: 'POST',
      body: JSON.stringify(instanceData),
    });
  }

  async updateInstance(id: number, instanceData: any) {
    return this.request(`/api/instances/${id}`, {
      method: 'PUT',
      body: JSON.stringify(instanceData),
    });
  }

  async deleteInstance(id: number) {
    return this.request(`/api/instances/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== DOUTORES ====================

  async getDoctors(params?: string | any) {
    if (typeof params === 'object') {
      const query = new URLSearchParams(params).toString();
      return this.request(`/api/doctors${query ? `?${query}` : ''}`);
    }
    return this.request(`/api/doctors${params ? `?${params}` : ''}`);
  }

  async getDoctor(id: number) {
    return this.request(`/api/doctors/${id}`);
  }

  async createDoctor(doctorData: any) {
    return this.request('/api/doctors', {
      method: 'POST',
      body: JSON.stringify(doctorData),
    });
  }

  async updateDoctor(id: number, doctorData: any) {
    return this.request(`/api/doctors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(doctorData),
    });
  }

  async deleteDoctor(id: number) {
    return this.request(`/api/doctors/${id}`, {
      method: 'DELETE',
    });
  }

  async getDoctorCalendarSettings(doctorId: number) {
    return this.request(`/api/doctors/${doctorId}/calendar-settings`);
  }

  async updateDoctorCalendarSettings(doctorId: number, settingsData: any) {
    return this.request(`/api/doctors/${doctorId}/calendar-settings`, {
      method: 'PUT',
      body: JSON.stringify(settingsData),
    });
  }

  async getDoctorCalendarConnections(doctorId: number) {
    return this.request(`/api/doctors/${doctorId}/calendar-connections`);
  }

  async saveDoctorCalendarConnection(doctorId: number, connectionData: any) {
    return this.request(`/api/doctors/${doctorId}/calendar-connections`, {
      method: 'POST',
      body: JSON.stringify(connectionData),
    });
  }

  async updateDoctorCalendarConnection(doctorId: number, calendarId: string, updateData: any) {
    return this.request(`/api/doctors/${doctorId}/calendar-connections/${encodeURIComponent(calendarId)}`, {
      method: 'PATCH',
      body: JSON.stringify(updateData),
    });
  }

  async deleteDoctorCalendarConnection(doctorId: number, calendarId: string) {
    return this.request(`/api/doctors/${doctorId}/calendar-connections/${encodeURIComponent(calendarId)}`, {
      method: 'DELETE',
    });
  }

  async createDoctorInternalUser(doctorId: number, payload: { name: string; email: string; clinic_id: number; password: string; }) {
    return this.request(`/api/doctors/${doctorId}/internal-user`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteDoctorInternalUser(doctorId: number, email: string) {
    const query = new URLSearchParams({ email }).toString();
    return this.request(`/api/doctors/${doctorId}/internal-user?${query}`, {
      method: 'DELETE',
    });
  }

  // ==================== ATENDENTES ====================

  async getAttendees(params?: string) {
    return this.request(`/api/attendees${params ? `?${params}` : ''}`);
  }

  async createAttendee(attendeeData: any) {
    return this.request('/api/attendees', {
      method: 'POST',
      body: JSON.stringify(attendeeData),
    });
  }

  async updateAttendee(id: number, attendeeData: any) {
    return this.request(`/api/attendees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(attendeeData),
    });
  }

  async deleteAttendee(id: number) {
    return this.request(`/api/attendees/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== CHAT ====================

  async getChatMessages(sessionId: string) {
    return this.request(`/api/chat/${sessionId}`);
  }

  async saveChatMessage(messageData: any) {
    return this.request('/api/chat', {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
  }

  // ==================== CONVERSATION MEMORY ====================

  async getConversationMemory(params?: {
    lead_id?: number;
    clinic_id?: number;
    instance_id?: number;
    limit?: number;
  }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request(`/api/conversation-memory${query ? `?${query}` : ''}`);
  }

  async saveConversationMemory(messageData: any) {
    return this.request('/api/conversation-memory', {
      method: 'POST',
      body: JSON.stringify(messageData),
    });
  }

  async clearConversationMemory(leadId: number) {
    return this.request(`/api/conversation-memory/${leadId}`, {
      method: 'DELETE',
    });
  }

  // ==================== CLINIC SETTINGS ====================

  async getClinicSettings(clinicId: number) {
    return this.request(`/api/clinic-settings/${clinicId}`);
  }

  async saveClinicSettings(settingsData: any) {
    return this.request('/api/clinic-settings', {
      method: 'POST',
      body: JSON.stringify(settingsData),
    });
  }

  // ==================== REMINDER LOGS ====================

  async getReminderLogs(appointmentId?: number) {
    const query = appointmentId ? `?appointment_id=${appointmentId}` : '';
    return this.request(`/api/reminder-logs${query}`);
  }

  async createReminderLog(logData: any) {
    return this.request('/api/reminder-logs', {
      method: 'POST',
      body: JSON.stringify(logData),
    });
  }

  // ==================== FILES ====================

  async getFiles(clinicId?: number) {
    const query = clinicId ? `?clinic_id=${clinicId}` : '';
    return this.request(`/api/files${query}`);
  }

  async registerFile(fileData: any) {
    return this.request('/api/files', {
      method: 'POST',
      body: JSON.stringify(fileData),
    });
  }

  async deleteFile(id: number) {
    return this.request(`/api/files/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== SCHEDULE BLOCKS ====================

  async getScheduleBlocks(params?: {
    doctor_id?: number;
    date?: string;
    clinic_id?: number;
  }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request(`/api/schedule-blocks${query ? `?${query}` : ''}`);
  }

  async createScheduleBlock(blockData: any) {
    return this.request('/api/schedule-blocks', {
      method: 'POST',
      body: JSON.stringify(blockData),
    });
  }

  async updateScheduleBlock(id: number, blockData: any) {
    return this.request(`/api/schedule-blocks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(blockData),
    });
  }

  async deleteScheduleBlock(id: number) {
    return this.request(`/api/schedule-blocks/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== CALENDAR SETTINGS ====================

  async getCalendarSettings(doctorId?: number) {
    const query = doctorId ? `?doctor_id=${doctorId}` : '';
    return this.request(`/api/calendar-settings${query}`);
  }

  async saveCalendarSettings(settingsData: any) {
    return this.request('/api/calendar-settings', {
      method: 'POST',
      body: JSON.stringify(settingsData),
    });
  }

  // ==================== DASHBOARD ====================

  async getDashboardMetrics() {
    return this.request('/api/dashboard/metrics');
  }

  // ==================== WHATSAPP ====================

  async sendWhatsAppMessage(instanceToken: string, to: string, message: string) {
    return this.request('/api/whatsapp/send-message', {
      method: 'POST',
      body: JSON.stringify({ instanceToken, to, message }),
    });
  }

  async sendWhatsAppMedia(
    instanceToken: string,
    to: string,
    mediaUrl: string,
    caption?: string,
    mediaType?: string
  ) {
    return this.request('/api/whatsapp/send-media', {
      method: 'POST',
      body: JSON.stringify({ instanceToken, to, mediaUrl, caption, mediaType }),
    });
  }

  async getWhatsAppInstanceStatus(token: string) {
    return this.request(`/api/whatsapp/instance-status/${token}`);
  }

  async connectWhatsAppInstance(instanceToken: string) {
    return this.request('/api/whatsapp/connect-instance', {
      method: 'POST',
      body: JSON.stringify({ instanceToken }),
    });
  }

  async getWhatsAppQRCode(token: string) {
    return this.request(`/api/whatsapp/qrcode/${token}`);
  }

  async disconnectWhatsAppInstance(instanceToken: string) {
    return this.request('/api/whatsapp/disconnect-instance', {
      method: 'POST',
      body: JSON.stringify({ instanceToken }),
    });
  }

  async restartWhatsAppInstance(instanceToken: string) {
    return this.request('/api/whatsapp/restart-instance', {
      method: 'POST',
      body: JSON.stringify({ instanceToken }),
    });
  }

  async deleteWhatsAppInstance(instanceToken: string) {
    return this.request('/api/whatsapp/delete-instance', {
      method: 'DELETE',
      body: JSON.stringify({ instanceToken }),
    });
  }

  // ==================== HEALTH CHECK ====================

  async healthCheck() {
    return this.request('/api/health');
  }

  // ==================== MÉTODOS AUXILIARES ====================

  /**
   * Método para fazer upload de arquivos
   */
  async uploadFile(file: File, endpoint: string = '/api/files/upload') {
    const formData = new FormData();
    formData.append('file', file);

    return this.request(endpoint, {
      method: 'POST',
      body: formData,
      headers: {
        // Não definir Content-Type para FormData - o browser define automaticamente
      },
    });
  }

  /**
   * Método para fazer requisições com timeout personalizado
   */
  async requestWithTimeout<T = any>(
    endpoint: string,
    options: RequestInit = {},
    timeoutMs: number = 30000
  ): Promise<ApiResponse<T>> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const result = await this.request<T>(endpoint, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return result;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Timeout: A requisição demorou muito para responder');
      }
      throw error;
    }
  }
}

const apiService = new ApiService();
export { apiService };
export default apiService;
